import React from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  get,
  TableColumn,
  Dejargonator,
  TextBody,
  Link as MuiLink,
} from '@ally/metronome-ui'
import { Row } from 'react-table'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import styled from 'styled-components'
import { Account } from '@ally/data-types-afg-accounts'
import dayjs from 'dayjs'
import { FeatureFlags } from '@ally/federated-types'
import {
  formatBalance,
  isAccountBlocked,
  mortgageAccountAllytmlnText,
  isMortgageAccountPaidInFull,
  isMortgageAccountTransferred,
  isMortgageAccountClaimsCloseOut,
  isMortgageAccountForeclosed,
  isMortgagePaidOffHandled,
  getAccountName,
} from '../../utils'
import { DashboardView } from '../../Customizations/types'
import {
  AccountDetailsLink,
  WarningIcon,
  TextBodyOverride,
  DashDash,
} from '../Shared'
import {
  DelinquentMortgageModal,
  PaidOffModal,
  NeedsAttentionActionModalTrigger,
  AccountNameModalTrigger,
} from './Modals'

export const PAID_OFF_ARIA_LABEL =
  'Loan has been paid off. Call to get more details.'
export const TRANSFERRED_ARIA_LABEL =
  'Loan has been transferred. Call to get more details.'
export const ALL_LOANS_PAID_OFF =
  'All loans have been paid off. Call to get more details.'

function footerTotal(
  allHomeLoanAccounts: Account[],
  type: 'current' | 'amountDue',
) {
  return (): React.ReactNode => {
    if (
      allHomeLoanAccounts.length &&
      allHomeLoanAccounts.every(isAccountBlocked)
    ) {
      return (
        <DashDash assistiveText="Total not available. Call for more information." />
      )
    }

    const total = React.useMemo(
      () =>
        allHomeLoanAccounts.reduce((sum, account) => {
          if (isAccountBlocked(account) || isMortgagePaidOffHandled(account)) {
            return sum
          }

          const metric: number | undefined =
            type === 'current'
              ? account.balance?.current
              : account.meta?.amountDue

          return (metric ?? 0) + sum
        }, 0),
      [],
    )

    return asCurrency(total)
  }
}

function getPaidOffDashDash(account: Account): React.ReactElement | null {
  if (isMortgageAccountPaidInFull(account)) {
    return <DashDash assistiveText={PAID_OFF_ARIA_LABEL} />
  }
  if (
    isMortgageAccountTransferred(account) ||
    isMortgageAccountClaimsCloseOut(account) ||
    isMortgageAccountForeclosed(account)
  ) {
    return <DashDash assistiveText={TRANSFERRED_ARIA_LABEL} />
  }

  return null
}

const StyledDejarg = styled(Dejargonator)`
  text-align: left;
  white-space: normal;
  text-transform: none;
`

const StyledDejargContent = styled(TextBody)`
  text-transform: capitalize;
  font-size: ${get.fontSizes('sm')};
  border-bottom: 1px dotted ${get.colors('slate-3')};
`

interface GetColumnsProps {
  tableTypeHeader: 'Mortgage' | 'Home Equity'
  view: DashboardView
  isLgDown: boolean
  allHomeLoanAccounts: Account[]
  featureFlags: FeatureFlags
}

export const getColumns = ({
  tableTypeHeader,
  view,
  isLgDown,
  allHomeLoanAccounts,
  featureFlags,
}: GetColumnsProps): TableColumn<Account>[] => {
  const isDefaultView = view === DashboardView.DEFAULT || isLgDown
  const columns = {
    accountName: {
      id: 'name',
      accessor: (account: Account): string => {
        return getAccountName(account.nickName, account.name)
      },
      Header: tableTypeHeader,
      Footer: 'Total',
      alignment: 'left',
      isSortable: true,
      Cell: ({
        row: { original: account },
      }: {
        row: Row<Account>
      }): React.ReactNode | string => {
        if (isAccountBlocked(account)) {
          return (
            <Box display="flex" alignItems="center" flexWrap="wrap">
              <Box display="inline-flex" alignItems="center">
                <WarningIcon style={{ flexShrink: 0 }} />
                <DelinquentMortgageModal
                  trigger={<AccountNameModalTrigger account={account} />}
                />
              </Box>
              <Box aria-hidden="true" style={{ flexShrink: 0 }}>
                {maskAccountNumber(account.accountNumber)}
              </Box>
            </Box>
          )
        }

        if (isMortgagePaidOffHandled(account)) {
          return (
            <>
              <PaidOffModal
                account={account}
                trigger={(ref): JSX.Element => (
                  <AccountNameModalTrigger account={account} focusRef={ref} />
                )}
              />
              <Box display="inline-block" aria-hidden="true">
                {maskAccountNumber(account.accountNumber)}
              </Box>
            </>
          )
        }

        return (
          <>
            <AccountDetailsLink
              text={getAccountName(account.nickName, account.name)}
              account={account}
              key={account.id}
              allytm={mortgageAccountAllytmlnText(account)}
            />
            <Box display="inline-block" aria-hidden="true">
              {maskAccountNumber(account.accountNumber)}
            </Box>
          </>
        )
      },
    },
    currentBalance: {
      id: 'currentBalance',
      accessor: 'balance.current',
      isSortable: true,
      Header: (
        <StyledDejarg
          trigger={{
            variant: 'element',
            content: (
              <StyledDejargContent tag="p" size="xs" weight="bold">
                Loan Balance
              </StyledDejargContent>
            ),
            ariaLabel: 'Select to learn more about your Loan Balance',
          }}
          ariaLabelPopover="Loan"
          body={
            <TextBodyOverride tag="p" size="sm" color="gray-400">
              Your{' '}
              <TextBodyOverride
                tag="span"
                size="sm"
                weight="bold"
                color="gray-400"
              >
                Loan Balance{' '}
              </TextBodyOverride>
              is the amount you owe on the original loan. It doesn&apos;t
              include interest, late fees, or other charges.
            </TextBodyOverride>
          }
        />
      ),
      alignment: 'right',
      Cell: ({
        row: { original: account },
      }: {
        row: Row<Account>
      }): React.ReactNode | string => {
        const paidOffDashDash = getPaidOffDashDash(account)
        if (paidOffDashDash) return paidOffDashDash

        const currentBalance = account.balance?.current ?? 0
        if (currentBalance >= 0 && !isAccountBlocked(account)) {
          return formatBalance(currentBalance)
        }

        return (
          <DashDash assistiveText="LOAN BALANCE not available. Call for more information." />
        )
      },
      Footer: (): React.ReactNode => {
        if (allHomeLoanAccounts.every(isMortgagePaidOffHandled)) {
          return <DashDash assistiveText={ALL_LOANS_PAID_OFF} />
        }
        return footerTotal(allHomeLoanAccounts, 'current')()
      },
    },
    amountDue: {
      id: 'amountDue',
      Header: 'Payment Due',
      alignment: 'right',
      isSortable: true,
      accessor: (account: Account): ReturnType<typeof formatBalance> => {
        if (isMortgagePaidOffHandled(account)) {
          return formatBalance(0)
        }

        const amountDue = account.meta?.amountDue ?? 0
        if (amountDue >= 0 && !isAccountBlocked(account)) {
          return formatBalance(amountDue)
        }

        return (
          <DashDash assistiveText="PAYMENT DUE not available. Call for more information." />
        )
      },
      Footer: footerTotal(allHomeLoanAccounts, 'amountDue'),
    },
    amountDueDate: {
      id: 'amountDueDate',
      accessor: 'meta.amountDueDate',
      Header: 'Due Date',
      alignment: 'right',
      isSortable: true,
      Cell: ({
        row: { original: account },
      }: {
        row: Row<Account>
      }): React.ReactNode | string => {
        if (isAccountBlocked(account))
          return (
            <DashDash assistiveText="DUE DATE not available. Call for more information." />
          )

        const paidOffDashDash = getPaidOffDashDash(account)
        if (paidOffDashDash) return paidOffDashDash

        const amountDueDateTime = account.meta?.amountDueDate
        if (!amountDueDateTime) {
          return null
        }

        // API is sending us a UTC time of 00:00 and when localized it can unintentionally alter the day/month
        const [timeStrippedDueDate] = amountDueDateTime.split('T')
        const amountDueDate = dayjs(timeStrippedDueDate).format('MMM DD, YYYY')

        return <span>{amountDueDate}</span>
      },
    },

    actions: {
      id: 'actions',
      Header: 'Actions',
      alignment: 'right',
      Cell: ({
        row: { original: account },
      }: {
        row: Row<Account>
      }): React.ReactElement<Element> => {
        if (isAccountBlocked(account)) {
          return (
            <DelinquentMortgageModal
              trigger={
                <NeedsAttentionActionModalTrigger accountType={account.type} />
              }
            />
          )
        }

        if (isMortgagePaidOffHandled(account)) {
          return <PaidOffModal account={account} />
        }

        const isMortgageDeeplinkingEnabled = featureFlags.variation(
          'FF_mortgage-deeplinking',
          false,
        )

        if (isMortgageDeeplinkingEnabled) {
          return (
            <MuiLink
              text="Make a Payment"
              allytmln={`${account.type}-make a payment`}
            >
              <Link
                to={`/sso/home-loans/payment?id=${account.accountNumber}`}
                aria-label={`Make a ${account.type} payment`}
              />
            </MuiLink>
          )
        }

        return (
          <MuiLink
            text="Manage Account"
            allytmln={`${account.type}-manage account`}
          >
            <Link
              to={`/sso/home-loans/dashboard?id=${account.accountNumber}`}
            />
          </MuiLink>
        )
      },
    },
  }

  const tileViewCols = [
    columns.accountName,
    columns.currentBalance,
    columns.amountDue,
    columns.actions,
  ] as TableColumn<Account>[]
  const defaultViewCols = [
    columns.accountName,
    columns.currentBalance,
    columns.amountDue,
    columns.amountDueDate,
    columns.actions,
  ] as TableColumn<Account>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
